import { createEntityAdapter, createSlice, PayloadAction, EntityState } from '@reduxjs/toolkit';

import { CaseCountInfo } from '@/effects/use-case-view-effect';

import { CaseMultiTabsModel } from '@/models/case-process.model';
import { TabViewsListModel } from '@/models/case-view.model';

export const DEFAULT_VIEW_ID = 'all_cases';

export type MultiTabUpdateModel = {
  id: string;
  changes: Partial<CaseMultiTabsModel>;
};

type CaseMultiTabsType = EntityState<CaseMultiTabsModel>;
export interface CaseMultiTabsState {
  panelVisible: boolean;
  // tab views
  viewList: TabViewsListModel[];
  activedViewId: string;
  showClosedStatusOption: boolean;
  caseCountMapper: Record<string, CaseCountInfo>;
  hasViewListInterfaceError: boolean;
  internalDB: CaseMultiTabsType;
  activeId: string;
  sortIds: string[];
}

export const multiTabsAdapter = createEntityAdapter<CaseMultiTabsModel>({
  selectId: (c) => c.id,
});

export const initialState: CaseMultiTabsState = {
  panelVisible: false,
  viewList: [],
  activedViewId: DEFAULT_VIEW_ID,
  showClosedStatusOption: true,
  caseCountMapper: {},
  hasViewListInterfaceError: false,
  internalDB: multiTabsAdapter.getInitialState(),
  activeId: '',
  sortIds: [],
};

const caseMultiTabsStore = createSlice({
  name: 'case-multi-tabs',
  initialState,
  reducers: {
    setPanelVisibles: (state, action: PayloadAction<CaseMultiTabsState['panelVisible']>) => {
      state.panelVisible = action.payload;
    },
    setViewLists: (state, action: PayloadAction<CaseMultiTabsState['viewList']>) => {
      state.viewList = action.payload;
    },
    setActivedViewId: (state, action: PayloadAction<CaseMultiTabsState['activedViewId']>) => {
      state.activedViewId = action.payload;
    },
    setShowClosedStatusOption: (state, action: PayloadAction<CaseMultiTabsState['showClosedStatusOption']>) => {
      state.showClosedStatusOption = action.payload;
    },
    setCaseCountMapper: (state, action: PayloadAction<CaseMultiTabsState['caseCountMapper']>) => {
      state.caseCountMapper = { ...state.caseCountMapper, ...action.payload };
    },
    setHasViewListInterfaceErrors: (state, action: PayloadAction<CaseMultiTabsState['hasViewListInterfaceError']>) => {
      state.hasViewListInterfaceError = action.payload;
    },
    multiTabsUpsertOne(state, action: PayloadAction<CaseMultiTabsModel>) {
      multiTabsAdapter.upsertOne(state.internalDB, action.payload);
    },
    multiTabsRemoveOne(state, action: PayloadAction<string>) {
      multiTabsAdapter.removeOne(state.internalDB, action.payload);
    },
    multiTabsSetAll(state, action: PayloadAction<CaseMultiTabsModel[]>) {
      multiTabsAdapter.setAll(state.internalDB, action.payload);
    },
    multiTabsRemoveAll(state) {
      multiTabsAdapter.removeAll(state.internalDB);
      state.activeId = '';
      state.sortIds = [];
    },
    multiTabsUpdateOne(state, action: PayloadAction<MultiTabUpdateModel>) {
      multiTabsAdapter.updateOne(state.internalDB, action.payload);
    },
    multiTabsUpdateMany(state, action: PayloadAction<MultiTabUpdateModel[]>) {
      multiTabsAdapter.updateMany(state.internalDB, action.payload);
    },
    multiTabsActiveIdSet(state, action: PayloadAction<CaseMultiTabsState['activeId']>) {
      state.activeId = action.payload;
    },
    multiTabsSortIdsSet(state, action: PayloadAction<CaseMultiTabsState['sortIds']>) {
      state.sortIds = action.payload;
    },
    multiTabsSortIdsAdd(state, action: PayloadAction<string>) {
      const _id = action.payload;
      const _index = state.sortIds.findIndex((item) => item === _id);
      if (_index < 0) {
        state.sortIds.unshift(_id);
      } else {
        // _index > 0 && state.sortIds.splice(_index, 1) && state.sortIds.unshift(_id);
      }
    },
    multiTabsSortIdsRemove(state, action: PayloadAction<string>) {
      const _id = action.payload;
      const _index = state.sortIds.findIndex((item) => item === _id);
      if (_index === 0) {
        state.sortIds.shift();
        multiTabsAdapter.removeOne(state.internalDB, _id);
        if (state.sortIds.length > 0 && state.activeId === _id) {
          state.activeId = state.sortIds[0];
        }
      } else if (_index > 0) {
        state.sortIds.splice(_index, 1);
        if (state.activeId === _id) {
          state.activeId = state.sortIds[_index - 1];
        }
        multiTabsAdapter.removeOne(state.internalDB, _id);
      }
      if (!state.sortIds.length) {
        state.activeId = '';
      }
    },
    resetMultiTabsDatas(state) {
      multiTabsAdapter.removeAll(state.internalDB);
      state.sortIds = [];
      state.activeId = '';
      state.panelVisible = false;
      state.viewList = [];
      state.activedViewId = DEFAULT_VIEW_ID;
    },
  },
});

export const {
  setPanelVisibles,
  setViewLists,
  setActivedViewId,
  setShowClosedStatusOption,
  setCaseCountMapper,
  setHasViewListInterfaceErrors,
  multiTabsUpsertOne,
  multiTabsRemoveOne,
  multiTabsSetAll,
  multiTabsActiveIdSet,
  multiTabsSortIdsSet,
  multiTabsSortIdsAdd,
  multiTabsSortIdsRemove,
  multiTabsRemoveAll,
  resetMultiTabsDatas,
  multiTabsUpdateOne,
  multiTabsUpdateMany,
} = caseMultiTabsStore.actions;
export default caseMultiTabsStore.reducer;
